import React, { useState } from "react";
import { Col, Container, Row } from "react-bootstrap";
import { ServiceSubcategoryList } from "../ServiceSubcategoryList/ServiceSubcategoryList";
import style from "../ServiceCategoryList/ServiceListComponent.module.scss";
import Button from "react-bootstrap/Button";
import { useTranslation } from "react-i18next";
import { SpinnerLoader, WithRoleAccess } from "@services/ui-components";
import { SimpleModal } from "@services/ui-components/src/SimpleModal/SimpleModal";
import { delModalCategoryContentType } from "../../../../interfaces/interfaces";
import ServiceValidation from "../../../../services/serviceCategory/ServiceValidation";
import { MoveModalComponent } from "../../MoveModalComponent/MoveModalComponent";
import { CreateEditModalComponent } from "@me-pos/service-modal";
import ServiceCategoryRequestInputService from "../../CreateService/utils/RequestInputService";
import Tooltip from "react-bootstrap/Tooltip";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import i18next from "i18next";
import { Link, useParams } from "react-router-dom";
import { useReactiveVar } from "@apollo/client";
import { CompanyAccess, ServiceCategoryNewInput, ServiceCategoryNewProxy } from "@me-team/host/main/graphql/types";
import {
    useDeleteServiceCategoryNewMutation,
    useRestoreServiceCategoryNewMutation,
    useUpdateServiceCategoryMutation,
} from "@me-team/host/main/serviceCategories/graphql/serviceCategories.hooks";
import { useCreateServCategoryMutation } from "@me-team/host/main/services/graphql/services.hooks";
import ToastComponent from "../../../ui-components/ToastComponent/ToastComponent";
import { useToast } from "../../../../hooks/useToast";
import { currentCompanyIdVar } from "@me-team/host/main/globalVar/state";
import { useRoleAccess } from "@me-team/host/src/utils/useRoleAccess";

type ServiceListComponentProps = {
    category: ServiceCategoryNewProxy;
    categoryList: ServiceCategoryNewProxy[];
    refreshCategoryListPerPage: () => void;
    toggleShowToast: (text: string) => void;
};

type CategoryModalAction = "create" | "edit" | "restore";

export const ServiceComponent: React.FC<ServiceListComponentProps> = (props) => {
    const { t: translate } = useTranslation();
    const { branchId } = useParams();
    const [isSubcategoryOpen, setIsSubcategoryOpen] = useState<boolean>(false);
    const [isDelModalOpen, setIsDelModalOpen] = useState(false);
    const [isMoveModalOpen, setIsMoveModalOpen] = useState<boolean>(false);
    const [delModalContent, setDelModalContent] = useState<delModalCategoryContentType>({
        title: "",
        description: "",
        withoutSubcatAndServices: true,
        withSubcat: false,
        withServiceInCategory: false,
        withServiceInSubcat: false,
    });
    const [isUpdateCategoryModalOpen, setIsUpdateCategoryModalOpen] = useState<boolean>(false);
    const [isCreateAction, setIsCreateAction] = useState<boolean>(false);
    const [categoryModalAction, setCategoryModalAction] = useState<CategoryModalAction>("edit");
    const currentCompanyId = useReactiveVar(currentCompanyIdVar);
    const { showToast, toastText, toggleShowToast, setShowToast } = useToast();
    const canCreateSubcategory = useRoleAccess(CompanyAccess.ServiceCategoryCreate);
    const canEditCategory = useRoleAccess(CompanyAccess.ServiceCategoryEdit);
    const canDeleteRestoreCategory = useRoleAccess(CompanyAccess.ServiceCategoryDeleteRestore);
    const canMoveCategory = !props.category.deleted && props.category.childCategories.length === 0 && canEditCategory;
    const canAddSubcategory = !props.category.deleted && canCreateSubcategory;
    const canEditVisibleCategory = !props.category.deleted && canEditCategory;
    const canDeleteVisibleCategory = !props.category.deleted && canDeleteRestoreCategory;
    const canRestoreCategory = props.category.deleted && canDeleteRestoreCategory;
    const hasVisibleCategoryActions =
        props.category.deleted ||
        canMoveCategory ||
        canAddSubcategory ||
        canEditVisibleCategory ||
        canDeleteVisibleCategory;
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [updateServiceCategory] = useUpdateServiceCategoryMutation({
        fetchPolicy: "network-only",
    });
    const [deleteServiceCategory] = useDeleteServiceCategoryNewMutation({
        fetchPolicy: "network-only",
    });
    const [restoreServiceCategory] = useRestoreServiceCategoryNewMutation({
        fetchPolicy: "network-only",
    });
    const [createCategory] = useCreateServCategoryMutation({
        fetchPolicy: "network-only",
    });

    const openSubcategoryHandler = () => {
        setIsSubcategoryOpen(!isSubcategoryOpen);
    };

    const handleOpenDelModal = () => {
        ServiceValidation.deleteCategoryValidator(props.category, setDelModalContent, translate);
        setIsDelModalOpen(true);
    };

    const handleCloseDelModal = () => {
        setIsDelModalOpen(false);
    };

    const deleteCategoryHandler = () => {
        setIsLoading(true);
        deleteServiceCategory({
            variables: { serviceCategoryId: props.category.id, branchId: +branchId },
            onCompleted: () => {
                props.refreshCategoryListPerPage();
                setIsDelModalOpen(false);
                setIsLoading(false);
                toggleShowToast(translate("Deleted"));
            },
        });
    };

    const onOpenMoveModal = () => {
        setIsMoveModalOpen(true);
    };

    const onCloseMoveModal = () => {
        setIsMoveModalOpen(false);
    };

    const moveCategoryHandler = (inputForReq: ServiceCategoryNewInput) => {
        updateServiceCategory({
            variables: { id: props.category.id, input: inputForReq, companyId: +currentCompanyId, branchId: +branchId },
            onCompleted: () => {
                props.refreshCategoryListPerPage();
                onCloseMoveModal();
                toggleShowToast(translate("Saved"));
            },
        });
    };

    const onSubmitHandlerRestoreCategory = () => {
        props.refreshCategoryListPerPage();
    };

    const onUpdateCategoryHandler = (action: CategoryModalAction) => {
        setCategoryModalAction(action);
        setIsCreateAction(action === "create");
        setIsUpdateCategoryModalOpen(true);
    };

    ////TODO Uncomment move category when all back will be ready
    return (
        <>
            <Container className="border-bottom table-category-width">
                <Row
                    className={`gx-3 ${hasVisibleCategoryActions ? "py-3" : "py-4"} flex-nowrap ps-4 ${props.category.deleted && "text-secondary"}`}
                >
                    <Col xs={3} className={"d-flex align-items-center"}>
                        {props.category.childCategories.length !== 0 && (
                            <button
                                className={`btn p-1 me-2 ${props.category.deleted && "text-secondary"}`}
                                onClick={openSubcategoryHandler}
                            >
                                <i className={`bi bi-chevron-right ${isSubcategoryOpen ? "rotated90" : ""} d-flex`}></i>
                            </button>
                        )}
                        {props.category.name}
                    </Col>
                    <Col xs={5} className={"d-flex align-items-center"}>
                        {props.category.comment ? props.category.comment : "—"}
                    </Col>
                    <Col xs={2} className={"d-flex align-items-center"}>
                        {props.category.services.length !== 0 ? (
                            <Link
                                to={`${currentCompanyId}/service-list/categoryId/${props.category.id}`}
                                className={"text-decoration-none"}
                            >
                                {props.category.services.length}
                            </Link>
                        ) : (
                            "-"
                        )}
                    </Col>
                    {hasVisibleCategoryActions && (
                        <>
                            {props.category.deleted ? (
                                <Col xs={2} className={"d-flex align-items-center justify-content-end pe-0"}>
                                    <OverlayTrigger
                                        placement="bottom"
                                        overlay={
                                            <Tooltip id="tooltip-top">
                                                {translate("servicesPage.categoryTooltip")}
                                            </Tooltip>
                                        }
                                    >
                                        <Button variant="secondary" className={"px-3 py-2 me-2 fw-normal rounded-1"}>
                                            {translate("servicesPage.archive")}
                                        </Button>
                                    </OverlayTrigger>
                                    <WithRoleAccess permission={CompanyAccess.ServiceCategoryDeleteRestore}>
                                        {canRestoreCategory && (
                                            <OverlayTrigger
                                                placement="bottom"
                                                overlay={<Tooltip id="tooltip-top">{translate("Restore")}</Tooltip>}
                                            >
                                                <Button
                                                    className={"btn rounded border text-dark bg-white rounded-1"}
                                                    onClick={() => {
                                                        onUpdateCategoryHandler("restore");
                                                    }}
                                                >
                                                    <i className="bi bi-arrow-counterclockwise d-flex fs-normal"></i>
                                                </Button>
                                            </OverlayTrigger>
                                        )}
                                    </WithRoleAccess>
                                </Col>
                            ) : (
                                <Col
                                    xs={2}
                                    className={"d-flex align-items-center justify-content-end flex-wrap gap-2 pe-0"}
                                >
                                    <WithRoleAccess permission={CompanyAccess.ServiceCategoryEdit}>
                                        {canMoveCategory && (
                                            <OverlayTrigger
                                                placement="bottom"
                                                overlay={
                                                    <Tooltip className="tooltip-category">
                                                        {translate("Move category")}
                                                    </Tooltip>
                                                }
                                            >
                                                <Button
                                                    className={"btn rounded text-dark bg-white border"}
                                                    onClick={onOpenMoveModal}
                                                >
                                                    <i className="bi bi-box-arrow-right d-flex fs-normal"></i>
                                                </Button>
                                            </OverlayTrigger>
                                        )}
                                    </WithRoleAccess>
                                    <WithRoleAccess permission={CompanyAccess.ServiceCategoryCreate}>
                                        {canAddSubcategory && (
                                            <OverlayTrigger
                                                placement="bottom"
                                                overlay={
                                                    <Tooltip className="tooltip-category">
                                                        {translate("Add subcategory")}
                                                    </Tooltip>
                                                }
                                            >
                                                <Button
                                                    className={"btn rounded border text-dark bg-white rounded-1"}
                                                    onClick={() => {
                                                        onUpdateCategoryHandler("create");
                                                    }}
                                                >
                                                    <i className="bi bi-plus-lg d-flex fs-normal"></i>
                                                </Button>
                                            </OverlayTrigger>
                                        )}
                                    </WithRoleAccess>
                                    <WithRoleAccess permission={CompanyAccess.ServiceCategoryEdit}>
                                        {canEditVisibleCategory && (
                                            <OverlayTrigger
                                                placement="bottom"
                                                overlay={
                                                    <Tooltip className="tooltip-category">
                                                        {translate("Edit category")}
                                                    </Tooltip>
                                                }
                                            >
                                                <Button
                                                    className={"btn rounded border text-dark bg-white rounded-1"}
                                                    onClick={() => {
                                                        onUpdateCategoryHandler("edit");
                                                    }}
                                                >
                                                    <i className="bi bi-pencil d-flex fs-normal"></i>
                                                </Button>
                                            </OverlayTrigger>
                                        )}
                                    </WithRoleAccess>
                                    <WithRoleAccess permission={CompanyAccess.ServiceCategoryDeleteRestore}>
                                        {canDeleteVisibleCategory && (
                                            <OverlayTrigger
                                                placement="bottom"
                                                overlay={<Tooltip>{translate("Delete category")}</Tooltip>}
                                            >
                                                <Button
                                                    className={"btn rounded border text-dark bg-white rounded-1"}
                                                    onClick={handleOpenDelModal}
                                                >
                                                    <i className="bi bi-trash3 d-flex fs-normal"></i>
                                                </Button>
                                            </OverlayTrigger>
                                        )}
                                    </WithRoleAccess>
                                </Col>
                            )}
                        </>
                    )}
                </Row>
            </Container>
            {props.category.childCategories.length !== 0 && (
                <div className={`${style.servicesSubcategoryList} ${isSubcategoryOpen ? style.open : ""}`}>
                    {props.category.childCategories.map((subcategory: ServiceCategoryNewProxy, index) => (
                        <ServiceSubcategoryList
                            key={index}
                            subcategory={subcategory}
                            parentCategory={props.category}
                            categoryList={props.categoryList}
                            refreshCategoryListPerPage={props.refreshCategoryListPerPage}
                        />
                    ))}
                </div>
            )}
            <SimpleModal
                isOpen={isDelModalOpen}
                onClose={handleCloseDelModal}
                title={delModalContent.title}
                description={delModalContent.description}
            >
                <Container>
                    <Row>
                        {(delModalContent.withoutSubcatAndServices || delModalContent.withSubcat) && (
                            <>
                                <Col xs={6} className={"ps-0"}>
                                    <Button
                                        variant="outline-primary"
                                        className={"w-100 fw-normal"}
                                        onClick={handleCloseDelModal}
                                    >
                                        {translate("servicesPage.deleteModal.cancelBtn")}
                                    </Button>
                                </Col>
                                <Col xs={6} className={"pe-0"}>
                                    <Button
                                        variant="primary"
                                        className={"w-100 fw-normal"}
                                        onClick={deleteCategoryHandler}
                                        disabled={isLoading}
                                    >
                                        {isLoading ? (
                                            <SpinnerLoader variant="light" loadingText={translate("Deleting...")} />
                                        ) : (
                                            translate("servicesPage.deleteModal.applyBtn")
                                        )}
                                    </Button>
                                </Col>
                            </>
                        )}

                        {delModalContent.withServiceInSubcat && (
                            <Col xs={12} className={"px-0"}>
                                <Button variant="primary" className={"w-100 fw-normal"} onClick={handleCloseDelModal}>
                                    {translate("servicesPage.deleteModal.okBtn")}
                                </Button>
                            </Col>
                        )}

                        {delModalContent.withServiceInCategory && (
                            <Col xs={12} className={"px-0"}>
                                <Link
                                    to={`${currentCompanyId}/service-list/categoryId/${props.category.id}`}
                                    className={"btn btn-primary w-100 fw-normal"}
                                >
                                    {translate("servicesPage.deleteModal.toServiceLink")}
                                </Link>
                            </Col>
                        )}
                    </Row>
                </Container>
            </SimpleModal>

            {isMoveModalOpen && (
                <MoveModalComponent
                    isMoveModalOpen={isMoveModalOpen}
                    onCloseMoveModal={onCloseMoveModal}
                    moveCategoryHandler={moveCategoryHandler}
                    currentCategory={props.category}
                    categoryList={props.categoryList}
                    requestInputService={ServiceCategoryRequestInputService}
                />
            )}
            {isUpdateCategoryModalOpen && (
                <CreateEditModalComponent
                    i18n={i18next}
                    categoryId={props.category.id}
                    subcategoryId={null}
                    isOpen={isUpdateCategoryModalOpen}
                    categoryInput={{
                        ...ServiceCategoryRequestInputService.createServiceCategoryInput(
                            props.category.id,
                            null,
                            props.categoryList
                        ),
                        deleted: categoryModalAction === "restore",
                    }}
                    categoryList={props.categoryList}
                    isCreateAction={isCreateAction}
                    onClose={() => {
                        setIsUpdateCategoryModalOpen(false);
                        setCategoryModalAction("edit");
                    }}
                    onSubmit={() => {}}
                    onSubmitCreate={async (inputVariables) => {
                        currentCompanyId &&
                            (await createCategory({
                                variables: { companyId: +currentCompanyId, input: inputVariables, branchId: +branchId },
                                onCompleted: () => {
                                    onSubmitHandlerRestoreCategory();
                                    toggleShowToast(translate("Created"));
                                },
                            }));
                    }}
                    onSubmitUpdate={async (inputVariables) => {
                        if (categoryModalAction === "restore") {
                            await restoreServiceCategory({
                                variables: {
                                    id: inputVariables.id,
                                    input: inputVariables.input,
                                    branchId: +branchId,
                                },
                                onCompleted: () => {
                                    onSubmitHandlerRestoreCategory();
                                    toggleShowToast(translate("Restored"));
                                },
                            });
                            return;
                        }

                        await updateServiceCategory({
                            variables: {
                                id: inputVariables.id,
                                input: inputVariables.input,
                                companyId: +currentCompanyId,
                                branchId: +branchId,
                            },
                            onCompleted: () => {
                                onSubmitHandlerRestoreCategory();
                                toggleShowToast(translate("Saved"));
                            },
                        });
                    }}
                    requestInputService={ServiceCategoryRequestInputService}
                    currentCategory={null}
                    categoryService={null}
                />
            )}
            {showToast && <ToastComponent show={showToast} setShow={setShowToast} text={toastText} />}
        </>
    );
};
